import { Box, Skeleton, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { isEmpty } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMeasure } from 'react-use';

import { EmptyStateWithIllustration } from '@/components/display/EmptyStateWithIllustration/EmptyStateWithIllustration';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import IllustrationBalanceSheetLight from '@/components/illustrations/IllustrationBalanceSheetLight.svg?react';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { BalanceSheetRowType } from '@/types/schema';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';
import { englishJoinStringList } from '@/utils/formatting/strings';

import {
  ENTITY_VALUE_COLUMN_ID,
  getViewConfigFilteredColumns,
  GRAND_TOTAL_COLUMN_ID,
  IN_ESTATE_TOTAL_COLUMN_ID,
  OUT_OF_ESTATE_TOTAL_COLUMN_ID,
} from './BalanceSheetTable.columns';
import {
  BALANCE_SHEET_TABLE_CLASS_NAME,
  RowData,
} from './BalanceSheetTable.constants';
import {
  BALANCE_SHEET_CARD_PADDING,
  BALANCE_SHEET_COLUMN_GAP_WIDTH,
  ENTITY_GRID_TEMPLATE_COLUMNS,
  HEADER_GROUP_ROW_HEIGHT,
} from './BalanceSheetTable.layout';
import { BalanceSheetDensity } from './BalanceSheetTable.types';
import {
  APP_ASSET_COLUMN,
  APP_DATA_COLUMN,
  buildFlatRows,
  calculateMaxVisibleColumns,
  ColumnGroup,
  ColumnSizeConfig,
  computeWidthTruncatedColumns,
  ENTITY_ASSET_COLUMN,
  ENTITY_DATA_COLUMN,
  EXPORT_ASSET_COLUMN,
  EXPORT_DATA_COLUMN,
  FlatRow,
  formatCellValue,
  getCellDescription,
  getCellPosition,
  getCellStyle,
  getSortedColumns,
  getSummaryVariant,
  mapDataToRows,
  PRESENTATION_ASSET_COLUMN,
  PRESENTATION_DATA_COLUMN,
  recalculateTotalColumns,
  splitNetWorthRows,
} from './BalanceSheetTable.utils';
import { useUnguardedBalanceSheetTableContext } from './BalanceSheetTableContext';
import { BalanceSheetCell } from './components/BalanceSheetCell';
import { BalanceSheetHeader } from './components/BalanceSheetHeader';
import { BalanceSheetHeaderGroup } from './components/BalanceSheetHeaderGroup';
import {
  BALANCE_SHEET_ROW_SHOW_ON_HOVER_CLASSNAME,
  BalanceSheetRow,
} from './components/BalanceSheetRow';
import { BalanceSheetTable_BalanceSheetFragment } from './graphql/BalanceSheetTable.fragments.generated';

export interface BalanceSheetTableProps {
  showEmptyState?: boolean;
  balanceSheet: BalanceSheetTable_BalanceSheetFragment | null;
  loading: boolean;
  /** When true, all expandable category rows start expanded */
  defaultExpandAll?: boolean;
  /** When true, bypass view configuration filtering and column truncation — show all columns */
  showAllColumns?: boolean;
  /** Row density: 'default' (48px), 'dense' (32px), 'extra-dense' (24px) */
  density?: BalanceSheetDensity;
  /** Pre-computed flat rows for paginated presentation rendering.
   *  When provided, bypasses internal row processing (mapDataToRows,
   *  splitNetWorthRows, buildFlatRows). */
  flatRowsOverride?: FlatRow[];
}

// ============================================================================
// Hooks
// ============================================================================

function useProcessedColumns(
  balanceSheet: BalanceSheetTable_BalanceSheetFragment | null,
  /** When true, bypass viewConfiguration filtering and show every column. */
  showAllColumns = false
) {
  const tableContext = useUnguardedBalanceSheetTableContext();

  return useMemo(() => {
    if (!balanceSheet) return { visibleColumns: [], columnGroups: [] };

    const filtered = showAllColumns
      ? balanceSheet.columns
      : getViewConfigFilteredColumns(
          balanceSheet.columns,
          tableContext?.viewConfiguration ?? null,
          {
            draftHiddenColumns: tableContext?.draftHiddenColumns,
          }
        );

    const sorted = getSortedColumns(
      filtered,
      tableContext?.draftColumnOrder ?? null
    );

    // Build column groups from the sorted columns
    const groupMap = new Map(
      balanceSheet.columnGroups.map((g) => [g.ID, g.display])
    );

    const groupOrder: string[] = [];
    const groupColumnsMap = new Map<string, typeof sorted>();

    for (const col of sorted) {
      if (!groupColumnsMap.has(col.group)) {
        groupOrder.push(col.group);
        groupColumnsMap.set(col.group, []);
      }
      // Safe to use non-null assertion: we just ensured the key exists above
      groupColumnsMap.get(col.group)!.push(col);
    }

    const columnGroups: ColumnGroup[] = groupOrder.map((groupId) => ({
      groupId,
      display: groupMap.get(groupId) || groupId,
      columns: groupColumnsMap.get(groupId) ?? [],
    }));

    return { visibleColumns: sorted, columnGroups };
  }, [
    balanceSheet,
    showAllColumns,
    tableContext?.viewConfiguration,
    tableContext?.draftColumnOrder,
    tableContext?.draftHiddenColumns,
  ]);
}

// ============================================================================
// Empty state & loading skeleton
// ============================================================================

function BalanceSheetTableEmptyState() {
  const canEdit = useCanEditHouseholdData();
  const [showAddHoldingModal, setShowAddHoldingModal] = useState(false);

  return (
    // Fill the leftover vertical space below the summary metrics row and center
    // the messaging within it (the parent chain grows via flex up to the
    // viewport-tall page box), rather than pinning it near the top.
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        alignSelf: 'stretch',
        minHeight: 0,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <EmptyStateWithIllustration
        Illustration={IllustrationBalanceSheetLight}
        title="No client data"
        description="There is no data associated with this client. As soon as data is entered, your balance sheet will appear."
        action={
          canEdit ? (
            <Button
              variant="primary"
              size="sm"
              startIcon={PlusIcon}
              onClick={() => setShowAddHoldingModal(true)}
            >
              Add holdings
            </Button>
          ) : undefined
        }
      />
      {showAddHoldingModal && (
        <AddHoldingsFormModal
          open={showAddHoldingModal}
          onClose={() => setShowAddHoldingModal(false)}
          holdingsGroup={undefined}
        />
      )}
    </Box>
  );
}

const SKELETON_ROW_COUNT = 8;
const SKELETON_ROW_HEIGHT = 48;

function BalanceSheetTableSkeleton() {
  return (
    <Box className={BALANCE_SHEET_TABLE_CLASS_NAME} sx={{ width: '100%' }}>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={SKELETON_ROW_HEIGHT}
          sx={{ width: '100%', mb: 0.5, borderRadius: 0.5 }}
        />
      ))}
    </Box>
  );
}

// ============================================================================
// Component
// ============================================================================

export function BalanceSheetTable({
  balanceSheet,
  loading,
  showEmptyState = true,
  defaultExpandAll = false,
  showAllColumns = false,
  density = 'default',
  flatRowsOverride,
}: BalanceSheetTableProps) {
  const isViewOnly = useViewOnly();
  const tableContext = useUnguardedBalanceSheetTableContext();
  const expandFromConfig =
    tableContext?.viewConfiguration?.showExpandedAssetCategories ?? false;
  const shouldExpandAll = defaultExpandAll || expandFromConfig;
  const { visibleColumns, columnGroups } = useProcessedColumns(
    balanceSheet,
    showAllColumns
  );

  // Measure container width for presentation mode column limiting
  const [measureRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();

  // Compute max height so the grid scrolls internally and sticky headers work.
  // Only applied in the app context (not presentation/export).
  // We use a state-based ref (instead of useRef) so that when the grid element
  // mounts after loading, the state update triggers the useLayoutEffect below.
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      setGridElement(node);
      measureRef(node as HTMLDivElement);
    },
    [measureRef]
  );

  useLayoutEffect(() => {
    if (isViewOnly) return;
    if (!gridElement) return;

    const updateHeight = () => {
      const rect = gridElement.getBoundingClientRect();
      const available =
        window.innerHeight - rect.top - BALANCE_SHEET_CARD_PADDING;
      setMaxHeight(Math.max(200, available));
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isViewOnly, gridElement]);

  const isEntityBalanceSheet =
    visibleColumns.length === 1 &&
    visibleColumns[0]?.ID === ENTITY_VALUE_COLUMN_ID;
  let assetSize: ColumnSizeConfig = APP_ASSET_COLUMN;
  let dataSize: ColumnSizeConfig = APP_DATA_COLUMN;
  if (showAllColumns) {
    assetSize = EXPORT_ASSET_COLUMN;
    dataSize = EXPORT_DATA_COLUMN;
  } else if (isEntityBalanceSheet) {
    assetSize = ENTITY_ASSET_COLUMN;
    dataSize = ENTITY_DATA_COLUMN;
  } else if (isViewOnly) {
    assetSize = PRESENTATION_ASSET_COLUMN;
    dataSize = PRESENTATION_DATA_COLUMN;
  }

  const showInEstate =
    showAllColumns || tableContext?.viewConfiguration?.showInEstate !== false;
  const showOutOfEstate =
    showAllColumns ||
    tableContext?.viewConfiguration?.showOutOfEstate !== false;
  const hasGrandTotalColumn = visibleColumns.some(
    (column) => column.ID === GRAND_TOTAL_COLUMN_ID
  );

  const maxColumns = useMemo(() => {
    // Only constrain columns in read-only (presentation) mode when
    // showAllColumns is not explicitly requested.
    if (showAllColumns || !isViewOnly) return Infinity;

    const gapCount =
      (showInEstate && showOutOfEstate ? 1 : 0) + (hasGrandTotalColumn ? 1 : 0);
    return calculateMaxVisibleColumns(
      containerWidth,
      assetSize.minWidth,
      dataSize.minWidth,
      gapCount * BALANCE_SHEET_COLUMN_GAP_WIDTH
    );
  }, [
    showAllColumns,
    isViewOnly,
    showInEstate,
    showOutOfEstate,
    hasGrandTotalColumn,
    containerWidth,
    assetSize.minWidth,
    dataSize.minWidth,
  ]);

  // Push maxVisibleColumns into context so the config panel can read it
  const setMaxVisibleColumns = tableContext?.setMaxVisibleColumns;
  useEffect(() => {
    if (isViewOnly && isFinite(maxColumns)) {
      setMaxVisibleColumns?.(maxColumns);
    }
    return () => {
      setMaxVisibleColumns?.(null);
    };
  }, [maxColumns, isViewOnly, setMaxVisibleColumns]);

  // In presentation mode, drop the columns that don't fit the page width and
  // rebuild the groups from what's left (total columns always keep their slot).
  const { truncatedGroups, hiddenColumns } = useMemo(() => {
    const { renderedColumns: fittedColumns, hiddenColumns } =
      computeWidthTruncatedColumns(visibleColumns, maxColumns);

    if (hiddenColumns.length === 0) {
      return { truncatedGroups: columnGroups, hiddenColumns };
    }

    // Rebuild column groups from the columns that fit
    const fittedIds = new Set(fittedColumns.map((c) => c.ID));
    const groups: ColumnGroup[] = [];
    for (const group of columnGroups) {
      const cols = group.columns.filter((c) => fittedIds.has(c.ID));
      if (cols.length > 0) {
        groups.push({ ...group, columns: cols });
      }
    }

    return { truncatedGroups: groups, hiddenColumns };
  }, [visibleColumns, columnGroups, maxColumns]);

  const renderedColumns = useMemo(
    () =>
      truncatedGroups.flatMap((group) =>
        group.columns.map((column, colIndex) => ({
          column,
          groupId: group.groupId,
          groupDisplay: group.display,
          isFirstInGroup: colIndex === 0,
          cellPosition: getCellPosition(column, group.columns),
        }))
      ),
    [truncatedGroups]
  );

  const allRows = useMemo(
    () =>
      splitNetWorthRows(
        recalculateTotalColumns(
          mapDataToRows(balanceSheet, {
            isViewOnly,
            includeChildren: shouldExpandAll,
          }),
          visibleColumns
        ),
        {
          showInEstate,
          showOutOfEstate,
          hasGrandTotalColumn,
        }
      ),
    [
      balanceSheet,
      hasGrandTotalColumn,
      isViewOnly,
      shouldExpandAll,
      showInEstate,
      showOutOfEstate,
      visibleColumns,
    ]
  );

  // Per-row overrides of `shouldExpandAll`. Storing only user overrides
  // (instead of the resolved expanded set) means Apollo refetches that give
  // `allRows` a new reference can't clobber user state — there is nothing
  // in state derived from `allRows`. `expandedSections` below is computed
  // at render time from these overrides plus `shouldExpandAll`.
  const [userToggles, setUserToggles] = useState<Map<string, boolean>>(
    () => new Map()
  );

  // Imperative reset when the user flips "expand all". The ref check guards
  // against no-op re-runs (and against the transient null-config flicker
  // during refetches that BalanceSheetTableContext also guards against).
  const prevShouldExpandAllRef = useRef(shouldExpandAll);
  useEffect(() => {
    if (prevShouldExpandAllRef.current !== shouldExpandAll) {
      prevShouldExpandAllRef.current = shouldExpandAll;
      setUserToggles(new Map());
    }
  }, [shouldExpandAll]);

  const expandedSections = useMemo(() => {
    const set = new Set<string>();
    for (const row of allRows) {
      if (!row.hasChildren) continue;
      if (userToggles.get(row.id) ?? shouldExpandAll) set.add(row.id);
    }
    return set;
  }, [allRows, userToggles, shouldExpandAll]);

  const toggleSection = useCallback(
    (sectionId: string) => {
      setUserToggles((prev) => {
        const next = new Map(prev);
        next.set(sectionId, !(next.get(sectionId) ?? shouldExpandAll));
        return next;
      });
    },
    [shouldExpandAll]
  );

  const handleAssociatedItemsClick = useCallback(
    (row: RowData) => {
      if (!isEmpty(row.entityIds)) {
        tableContext?.setSelectedEntitiesRow({
          entityIds: row.entityIds ?? [],
          row,
        });
      } else if (row.liabilityId) {
        tableContext?.setSelectedLiabilityRow({
          liabilityId: row.liabilityId,
          row,
        });
      }
    },
    [tableContext]
  );

  // Build the CSS Grid column template so that every row shares the same
  // column tracks, guaranteeing alignment without JS width calculations.
  const gridTemplateColumns = useMemo(() => {
    if (isEntityBalanceSheet) return ENTITY_GRID_TEMPLATE_COLUMNS;
    const assetTrack = assetSize.maxWidth
      ? `minmax(${assetSize.minWidth}px, ${assetSize.maxWidth}px)`
      : `minmax(${assetSize.minWidth}px, 1fr)`;
    const dataTrack = dataSize.maxWidth
      ? `minmax(${dataSize.minWidth}px, ${dataSize.maxWidth}px)`
      : `minmax(${dataSize.minWidth}px, 1fr)`;

    const tracks = [assetTrack];
    for (const { column } of renderedColumns) {
      tracks.push(dataTrack);
      if (
        column.ID === IN_ESTATE_TOTAL_COLUMN_ID &&
        (showOutOfEstate || hasGrandTotalColumn)
      ) {
        tracks.push(`${BALANCE_SHEET_COLUMN_GAP_WIDTH}px`);
      }
      if (column.ID === OUT_OF_ESTATE_TOTAL_COLUMN_ID && hasGrandTotalColumn) {
        tracks.push(`${BALANCE_SHEET_COLUMN_GAP_WIDTH}px`);
      }
    }
    return tracks.join(' ');
  }, [
    assetSize,
    dataSize,
    hasGrandTotalColumn,
    renderedColumns,
    showOutOfEstate,
    isEntityBalanceSheet,
  ]);

  const computedFlatRows = useMemo(
    () => buildFlatRows(allRows, expandedSections),
    [allRows, expandedSections]
  );

  const effectiveFlatRows = flatRowsOverride ?? computedFlatRows;

  // Width-truncated columns stay in the totals (presentation only); surface
  // them with a footnote naming the hidden columns and their combined value.
  const widthHiddenFootnote = useMemo(() => {
    if (!isViewOnly || hiddenColumns.length === 0) return null;
    const subtotalsRow = allRows.find((r) => r.displayVariant === 'SUBTOTALS');
    const hiddenValue = hiddenColumns.reduce(
      (sum, col) =>
        sum.plus(subtotalsRow?.values[col.ID]?.value ?? new Decimal(0)),
      new Decimal(0)
    );
    return {
      hiddenValue,
      names: hiddenColumns.map((col) => col.display),
    };
  }, [isViewOnly, hiddenColumns, allRows]);

  // True only for the real Total net worth row (the Subtotals row has
  // displayVariant 'SUBTOTALS'), so the footnote renders only when the
  // asterisked row is on the page.
  const renderedHasNetWorthRow = effectiveFlatRows.some(
    ({ row }) =>
      (row.displayVariant ?? row.variant) === BalanceSheetRowType.NetWorth
  );

  if (balanceSheet?.hasNoValues && showEmptyState) {
    return <BalanceSheetTableEmptyState />;
  }

  // Only show the skeleton on initial load. During refetches, show stale data
  // to avoid a jarring flash since refetches typically resolve in ~100-200ms.
  if (loading && !balanceSheet) {
    return <BalanceSheetTableSkeleton />;
  }

  if (!balanceSheet || visibleColumns.length === 0) {
    return null;
  }

  const table = (
    <Box
      ref={containerRef}
      data-testid="balance-sheet-table"
      className={BALANCE_SHEET_TABLE_CLASS_NAME}
      sx={{
        display: 'grid',
        gridTemplateColumns,
        width: showAllColumns ? 'max-content' : '100%',
        // Use 'visible' for export pages so Chrome's print engine can break
        // pages inside the grid. 'auto' creates a scroll container that
        // Chrome treats as atomic, clipping content that overflows one page.
        overflow: showAllColumns ? 'visible' : 'auto',
        ...(!isViewOnly && maxHeight != null && { maxHeight }),
      }}
    >
      {/* Header Group Row — renders one slot per column so that flex
          sizing matches the column header and data rows exactly */}
      {!isEntityBalanceSheet && (
        <BalanceSheetRow
          enableHover={false}
          variant={isViewOnly ? 'contents' : 'stickyHeader'}
          stickyTop={0}
        >
          {/* Empty spacer above Asset column — grid sizes this via the track */}
          <Box />
          {renderedColumns.map(
            ({
              column,
              groupDisplay,
              isFirstInGroup,
              cellPosition,
              groupId,
            }) => (
              <BalanceSheetHeaderGroup
                key={column.ID}
                text={isFirstInGroup ? groupDisplay : undefined}
                density={density}
                cellPosition={cellPosition}
                alignment={groupId === GRAND_TOTAL_COLUMN_ID ? 'right' : 'left'}
                isPresentation={isViewOnly}
                hasColumnGap={
                  (column.ID === IN_ESTATE_TOTAL_COLUMN_ID &&
                    (showOutOfEstate || hasGrandTotalColumn)) ||
                  (column.ID === OUT_OF_ESTATE_TOTAL_COLUMN_ID &&
                    hasGrandTotalColumn)
                }
              />
            )
          )}
        </BalanceSheetRow>
      )}

      {/* Column Header Row */}
      <BalanceSheetRow
        enableHover={false}
        variant={isViewOnly ? 'contents' : 'stickyHeader'}
        stickyTop={isEntityBalanceSheet ? 0 : HEADER_GROUP_ROW_HEIGHT}
      >
        <BalanceSheetHeader
          cellPosition="start"
          density={density}
          alignment="left"
          text="Assets"
          isPresentation={isViewOnly}
        />
        {isEntityBalanceSheet && (
          <BalanceSheetHeader
            cellPosition="middle"
            density={density}
            alignment="left"
            text="Details"
            isPresentation={isViewOnly}
          />
        )}
        {renderedColumns.map(({ column, cellPosition }) => (
          <BalanceSheetHeader
            key={column.ID}
            cellPosition={cellPosition}
            density={density}
            alignment="right"
            text={column.display}
            hasColumnGap={
              (column.ID === IN_ESTATE_TOTAL_COLUMN_ID &&
                (showOutOfEstate || hasGrandTotalColumn)) ||
              (column.ID === OUT_OF_ESTATE_TOTAL_COLUMN_ID &&
                hasGrandTotalColumn)
            }
            isPresentation={isViewOnly}
          />
        ))}
      </BalanceSheetRow>

      {/* Data Rows */}
      {effectiveFlatRows.map(({ row, isChild, isLastChild }) => {
        const cellStyle = getCellStyle(row, isLastChild);
        const summaryVariant = getSummaryVariant(row);
        const isExpanded = expandedSections.has(row.id);
        const hasAssociatedNodes = !isEmpty(row.entityIds) || !!row.liabilityId;

        return (
          <BalanceSheetRow key={row.id} enableHover>
            {/* Asset name column — position:relative so the hover icon overlays
                without displacing the cell */}
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
              }}
            >
              <BalanceSheetCell
                cellStyle={cellStyle}
                cellPosition="start"
                density={density}
                alignment="left"
                lineOne={row.name.lineOne}
                lineTwo={isEntityBalanceSheet ? undefined : row.name.lineTwo}
                hasExpandIcon={
                  row.variant === BalanceSheetRowType.Category &&
                  row.hasChildren &&
                  !isViewOnly
                }
                isExpanded={isExpanded}
                onExpandClick={() => toggleSection(row.id)}
                indentLevel={isChild ? 1 : 0}
                isPresentation={isViewOnly}
              />
              {hasAssociatedNodes && !isViewOnly && (
                <Box
                  className={BALANCE_SHEET_ROW_SHOW_ON_HOVER_CLASSNAME}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <IconButton
                    icon={LinkExternal01Icon}
                    ariaLabel="View associated entities"
                    size="xs"
                    variant="transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociatedItemsClick(row);
                    }}
                  />
                </Box>
              )}
            </Box>

            {isEntityBalanceSheet && (
              <BalanceSheetCell
                testId={`balance-sheet-details-${row.id}`}
                cellStyle={cellStyle}
                cellPosition="middle"
                density={density}
                alignment="left"
                lineOne={
                  row.variant === BalanceSheetRowType.LineItem
                    ? (row.name.lineTwo ?? '')
                    : ''
                }
                multiline
                isPresentation={isViewOnly}
              />
            )}

            {/* Data columns */}
            {renderedColumns.map(({ column, cellPosition }) => {
              const cellValue = formatCellValue(row, column.ID);
              const rowVariant = row.displayVariant ?? row.variant;
              // Asterisk the Total net worth row only (the Subtotals row shares
              // variant NetWorth but has displayVariant 'SUBTOTALS').
              const withAsterisk =
                !!widthHiddenFootnote &&
                rowVariant === BalanceSheetRowType.NetWorth &&
                cellValue !== '';
              let cellTestId: string | undefined;
              if (rowVariant === 'SUBTOTALS') {
                cellTestId = `balance-sheet-subtotal-${column.ID}`;
              } else if (
                rowVariant === BalanceSheetRowType.NetWorth &&
                cellValue !== ''
              ) {
                cellTestId = 'balance-sheet-total-net-worth';
              }
              return (
                <BalanceSheetCell
                  key={column.ID}
                  testId={cellTestId}
                  cellStyle={cellStyle}
                  cellPosition={cellPosition}
                  density={density}
                  alignment="right"
                  lineOne={cellValue}
                  superscript={withAsterisk ? '*' : undefined}
                  lineTwo={getCellDescription(row, column.ID)}
                  hasColumnGap={
                    (column.ID === IN_ESTATE_TOTAL_COLUMN_ID &&
                      (showOutOfEstate || hasGrandTotalColumn)) ||
                    (column.ID === OUT_OF_ESTATE_TOTAL_COLUMN_ID &&
                      hasGrandTotalColumn)
                  }
                  summaryVariant={summaryVariant}
                  isPresentation={isViewOnly}
                />
              );
            })}
          </BalanceSheetRow>
        );
      })}
    </Box>
  );

  if (!widthHiddenFootnote || !renderedHasNetWorthRow) {
    return table;
  }

  return (
    <>
      {table}
      <Typography variant="body-xs" sx={{ mt: 1 }}>
        {`* The total net worth reflected on the balance sheet includes ${formatCurrencyNoDecimalsAccounting(
          widthHiddenFootnote.hiddenValue
        )} that is hidden from view and consists of ${englishJoinStringList(
          widthHiddenFootnote.names
        )}.`}
      </Typography>
    </>
  );
}
